"use client";
import { DollarSign, Package, Users } from "lucide-react";
import { Summary } from "../../pages/SalesPage/SalesPageInterface";
import { formatCurrency, getCurrencySymbol } from "../../utils/formatters";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";

interface CardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  isCurrency?: boolean;
}

const StatCard = ({ title, value, icon, isCurrency = false, currencySymbol = "$" }: CardProps & { currencySymbol?: string }) => {

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold mt-1">
            {isCurrency ? formatCurrency(value, currencySymbol) : value}
          </p>
        </div>
        <div className="p-3 bg-blue-50 rounded-lg">{icon}</div>
      </div>
    </div>
  );
};

interface SalesStatsProps {
  summary: Summary;
}

const SalesStats = ({ summary }: SalesStatsProps) => {
  const { language, country } = useAuth();
  const currencySymbol = getCurrencySymbol(country);

  const stats = [
    {
      title: dictionary["total-sales"][language],
      value: Number(summary?.total_value_with_discount || 0),
      icon: <DollarSign className="text-fluxi" size={24} />,
      isCurrency: true,
    },
    {
      title: dictionary["products-sold"][language],
      value: summary?.total_units || 0,
      icon: <Package className="text-fluxi" size={24} />,
    },
    {
      title: dictionary["buyers"][language],
      value: summary?.total_buyers || 0,
      icon: <Users className="text-fluxi" size={24} />,
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      {stats.map((s, i) => (
        <StatCard
          key={i}
          title={s.title}
          value={Number(s.value)}
          icon={s.icon}
          isCurrency={s.isCurrency}
          currencySymbol={currencySymbol}
        />
      ))}
    </div>
  );
};

export default SalesStats;
